<script setup>
import { useVModels } from "@vueuse/core"
import { computed, ref, watch } from "vue"
import { PencilSquareIcon } from '@heroicons/vue/24/solid'
import { PrimaryButton, Select, Text, Number, Error } from "@codinglabsau/ui"
import useValidatesNodeProps from "@/Composables/useValidatesNodeProps"
import Modal from "@/Components/Inputs/BlockEditor/Extension/Components/Modal.vue"
import ToolbarButton from "@/Components/Inputs/BlockEditor/Extension/Components/ToolbarButton.vue"

const props = defineProps({
  src: {
    required: true,
  },
  height: {
    required: true,
  },
  width: {
    required: true,
  },
  justify: {
    required: true,
  },
})

let showDialogue = ref(false)
const emits = defineEmits(['update:src', 'update:height', 'update:width', 'update:justify'])

const {
  src: computedSrc,
  height: computedHeight,
  justify: computedJustify,
} = useVModels(props, emits)

const urlPattern = /^(https?):\/\/[^\s/$.?#].[^\s]*$/i

const srcRules = [
  {
    validator: (value) => urlPattern.test(value),
    message: __('Source must be a valid URL'),
  },
]

const {
  internalValue: srcInput,
  isValid: srcIsValid,
  error: srcError,
} = useValidatesNodeProps(computedSrc, srcRules)

const canSave = computed(() => srcIsValid.value)

const widthControlOptions = ['full', 'custom']
const widthControl = ref('custom')

watch(widthControl, (value) => {
  if (value === 'full') {
    emits('update:width', '100%')
  } else {
    emits('update:width', '640px')
  }
})

// getters for width and its unit
const computedWidth = computed(() => props.width.replace(/\D+/g, ''))
const computedWidthUnit = computed(() => props.width.replace(/\d+/g, ''))

const widthModel = computed({
  get() {
    return computedWidth.value
  },
  set(value) {
    emits('update:width', `${value}${computedWidthUnit.value}`)
  }
})

const widthUnitModel = computed({
  get() {
    return computedWidthUnit.value
  },
  set(value) {
    emits('update:width', `${computedWidth.value}${value}`)
  }
})

watch(widthUnitModel, (newVal, oldVal) => {
  if (oldVal === 'px' && newVal === '%' && computedWidth.value > 100) {
    emits('update:width', `100%`)
  }
})
</script>

<template>
  <ToolbarButton
    :icon="PencilSquareIcon"
    :label="__('Edit IFrame')"
    v-model="showDialogue"
  />
  <Modal v-model="showDialogue">
    <template #title>
      {{ __('Edit IFrame') }}
    </template>
    <div>
      <label for="src">{{ __('IFrame Source') }}</label>
      <Text v-model="srcInput" description="Paste link here"/>
      <Error :error="srcError"/>
    </div>
    <div>
      <label for="height"> {{ __('Height') }}</label>
      <Number v-model="computedHeight" step="50"
              description="Use the increment/decrement arrows for a faster experience"/>
    </div>
    <div>
      <label for="width"> {{ __('Width') }}</label>
      <div class="flex flex-col space-y-1">
        <label v-for="option in widthControlOptions" class="capitalize">
          <input
            v-model="widthControl"
            :value="option"
            class="mr-2"
            type="radio"
            name="widthControl"
          >
          {{ option }}
        </label>
      </div>
      <div class="flex gap-1 w-fit">
        <Number
          v-if="widthControl === 'custom'"
          v-model="widthModel"
        />
        <Select
          v-if="widthControl === 'custom'"
          v-model="widthUnitModel"
          class="pr-8"
        >
          <option value="px" selected>px</option>
          <option value="%">%</option>
        </Select>
      </div>
    </div>
    <div>
      <label for="justify">{{ __('Justify item') }}</label>
      <Select v-model="computedJustify">
        <option value="start" selected>{{ __('Start') }}</option>
        <option value="end">{{ __('End') }}</option>
        <option value="center">{{ __('Center') }}</option>
      </Select>
    </div>
    <PrimaryButton
      class="mt-2"
      type="button"
      as="button"
      :disabled="!canSave"
      @click.prevent="showDialogue = false"
    >
      {{ __('Done') }}
    </PrimaryButton>
  </Modal>
</template>
